import React from 'react';
import { Header, Logo } from '@vwfs-bronson/bronson-react';
import { SiteNav, SiteNavProps } from './SiteNav';
import './NavigationBar.css';

export type NavigationBarProps = {
    /**
     * Alternative text for the logo (e.g. Home or Dashboard)
     */
    logoAltText: string;
    /**
     * Enables onClick for logo
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    onLogoClick?: (e: any) => void;
    /**
     * Disables link in logo
     */
    logoDisableLink?: boolean | undefined;
    /**
     * Path to page for on logo click (e.g. Dashboard)
     */
    onLogoClickPath?: string | undefined;
    /**
     * The navigation description
     */
    navigation: SiteNavProps;
    /**
     * The language switcher definition (@see LanguageSwitcher)
     */
    languageSwitcher?: React.ReactNode;
    /**
     * Optional language switcher button position. Default 'last'
     * Option 'first' shows language switcher before navigation items
     * Option 'last' shows language switcher after navigation items
     * Note: To disable css reordering use LanguageSwitcher (@see LanguageSwitcher) with `topAtBreakpoint={false}` flag
     */
    languageSwitcherOrder?: 'first' | 'last';
    /**
     * Enables the static header on the navbar
     */
    withStaticHeader?: boolean;

    /**
     * Hides Navbar completely
     */
    hidden?: boolean;
    /**
     * Custom logo source
     */
    logoSource?: string;
    logoClassName?: string;

    /**
     * Notifications Center component
     */
    notificationsCenter?: React.ReactNode;

    /**
     * Optional close menu text for VWFS brand ("Close Menu" when omitted)
     */
    closeMenuText?: string;
    /**
     * Optional custom title for open button ("Show navigation" when omitted)
     */
    openButtonTitle?: string;

    /**
     * Optional open menu text for VW6 brand ("Menu" when omitted)
     */
    openMenuText?: string;

    /**
     * Optional alt for logo
     */
    alt?: string;

    /**
     * Optional href for logo
     */
    href?: string;

    /**
     * Optional ariaLabel
     */
    ariaLabel?: string;
};

/**
 * A simple navigation bar including logo definition and an optional language switcher
 */
export const NavigationBar: React.FC<NavigationBarProps> = ({
    logoAltText,
    navigation,
    onLogoClickPath,
    logoDisableLink,
    languageSwitcher,
    withStaticHeader,
    hidden,
    logoSource,
    logoClassName,
    notificationsCenter,
    closeMenuText,
    openButtonTitle,
    openMenuText,
    alt,
    href,
    ariaLabel,
    onLogoClick,
    languageSwitcherOrder = 'last',
}) => {
    if (hidden) {
        return null;
    }

    const logo = logoSource ? (
        <Logo
            disableLink={logoDisableLink}
            onClick={onLogoClick}
            title={logoAltText}
            aria-label={ariaLabel ?? logoAltText}
            src={logoSource}
            className={logoClassName}
            aria-hidden={!!onLogoClickPath ? 'false' : 'true'}
            alt={alt}
            href={href}
        />
    ) : (
        <Logo
            disableLink={logoDisableLink}
            onClick={onLogoClick}
            title={logoAltText}
            aria-label={ariaLabel ?? logoAltText}
            aria-hidden={!!onLogoClickPath ? 'false' : 'true'}
            href={href}
        />
    );

    return (
        <Header logo={logo} staticHeader={withStaticHeader} notificationCenter={notificationsCenter}>
            <SiteNav
                navigationItems={navigation.navigationItems}
                languageSwitcher={languageSwitcher}
                languageSwitcherOrder={languageSwitcherOrder}
                closeMenuText={closeMenuText}
                openButtonTitle={openButtonTitle}
                openMenuText={openMenuText}
            />
        </Header>
    );
};
